import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────
// ASSETS — put in src/assets/ :
//   src/assets/escort-hero.jpeg     (wide convoy photo, blue sky)
//   src/assets/pilotlink-logo.jpeg  (optional — wordmark is code-drawn)
// ─────────────────────────────────────────────────────────────────────
import heroTruck from '../assets/logos/hero1.png';

// ─────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — single enterprise palette: NAVY + BLUE + SLATE
//   The amber/orange accent has been removed. Everything now keys off
//   the brand navy (#0d1f3c — same as the Navbar gradient and the logo)
//   with blue as the one action color, and a muted brick red reserved
//   exclusively for the "USA" in the logo.
// ─────────────────────────────────────────────────────────────────────
const DARK = {
  bg:        "#0d1f3c",
  surface:   "#142a4d",
  surface2:  "#1a3358",
  border:    "rgba(255,255,255,0.10)",
  borderSoft:"rgba(255,255,255,0.06)",
  accent:    "#3b82f6",              // blue-500 — sole action color
  accentL:   "#60a5fa",              // blue-400
  accentBg:  "rgba(59,130,246,0.12)",
  green:     "#34d399",
  greenBg:   "rgba(52,211,153,0.10)",
  redMuted:  "#e05252",              // toned-down brand red (logo only)
  text:      "#f0f4f8",
  textMid:   "rgba(240,244,248,0.78)",
  textSoft:  "rgba(240,244,248,0.50)",
  logoMain:  "#f0f4f8",
};

const LIGHT = {
  bg:        "#f8fafc",              // slate-50 — matches App.js
  surface:   "#ffffff",
  surface2:  "#f1f5f9",              // slate-100
  border:    "#e2e8f0",              // slate-200 — matches Navbar
  borderSoft:"#f1f5f9",
  accent:    "#2563eb",              // blue-600 — sole action color
  accentL:   "#3b82f6",
  accentBg:  "rgba(37,99,235,0.08)",
  green:     "#16a34a",
  greenBg:   "rgba(22,163,74,0.08)",
  redMuted:  "#b83c3c",              // toned-down brand red (logo only)
  text:      "#0f172a",
  textMid:   "#475569",
  textSoft:  "#94a3b8",
  logoMain:  "#122c54",
};

const NAVY = "#0d1f3c";

// Placeholder partner logos — swap the icon/name pairs for real partner
// logos (as <img> tags) once you have assets. Spans the industries that
// actually surround escort/pilot car work, not just carriers, since
// insurance, equipment, vehicles, and tech vendors partner here too.
// Names are intentionally fictional/generic so nothing implies a real
// partnership that doesn't exist yet.
const PARTNER_LOGOS = [
  { name: "Frontier Carriers",        icon: "🛣️", category: "Carrier" },
  { name: "Highway Shield Insurance", icon: "🛡️", category: "Insurance" },
  { name: "TopGear Escort Equipment", icon: "🦺", category: "Equipment" },
  { name: "Vanguard Fleet Vehicles",  icon: "🚙", category: "Vehicles" },
  { name: "Launchpad Business Co.",   icon: "🚀", category: "Startup Services" },
  { name: "RouteSync Technologies",   icon: "📡", category: "Software & Tech" },
  { name: "Beacon Freight Co.",       icon: "🔦", category: "Carrier" },
  { name: "Convoy Media Partners",    icon: "📣", category: "Marketing & Promotion" },
];

// ─────────────────────────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────────────
function Card({ T, children, style, to }) {
  const cardStyle = {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: 14,
    overflow: "hidden",
    textDecoration: "none",
    color: "inherit",
    display: "block",
    transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
    ...style,
  };
  if (to) return <Link to={to} className="pl-card" style={cardStyle}>{children}</Link>;
  return <div className="pl-card" style={cardStyle}>{children}</div>;
}

// Primary action — solid blue. One action color across the whole page.
function PrimaryBtn({ T, children, onClick, style, to }) {
  const btnStyle = {
    background: T.accent,
    border: "none", borderRadius: 8,
    padding: "clamp(13px, 2vw, 15px) clamp(22px, 4vw, 34px)",
    fontSize: "clamp(14px, 1.5vw, 16px)", fontWeight: 700, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    textDecoration: "none", cursor: "pointer", textAlign: "center",
    flex: "1 1 auto", minWidth: "max-content",
    boxShadow: `0 4px 14px ${T.accentBg}`,
    ...style,
  };
  if (to) return <Link to={to} className="pl-btn" style={btnStyle}>{children}</Link>;
  return <button className="pl-btn" onClick={onClick} style={btnStyle}>{children}</button>;
}

// Secondary action — quiet outline, lets the primary lead.
function SecondaryBtn({ T, children, onClick, style, to, onNavy }) {
  const btnStyle = {
    background: "transparent",
    border: `1.5px solid ${onNavy ? "rgba(255,255,255,0.35)" : T.border}`,
    borderRadius: 8,
    padding: "clamp(13px, 2vw, 15px) clamp(22px, 4vw, 34px)",
    fontSize: "clamp(14px, 1.5vw, 16px)", fontWeight: 700,
    color: onNavy ? "#f0f4f8" : T.text,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    textDecoration: "none", cursor: "pointer", textAlign: "center",
    flex: "1 1 auto", minWidth: "max-content",
    ...style,
  };
  if (to) return <Link to={to} className="pl-btn-ghost" style={btnStyle}>{children}</Link>;
  return <button className="pl-btn-ghost" onClick={onClick} style={btnStyle}>{children}</button>;
}

// Accent-tinted action — for the "Become a Partner" CTA. Same blue accent
// as everything else (not a new color), but a filled tint + solid border
// makes it read as its own distinct option next to the driver/company
// buttons instead of blending into the plain outline style.
function AccentBtn({ T, children, onClick, style, to }) {
  const btnStyle = {
    background: T.accentBg,
    border: `1.5px solid ${T.accent}`,
    borderRadius: 8,
    padding: "clamp(13px, 2vw, 15px) clamp(22px, 4vw, 34px)",
    fontSize: "clamp(14px, 1.5vw, 16px)", fontWeight: 700,
    color: T.accent,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    textDecoration: "none", cursor: "pointer", textAlign: "center",
    flex: "1 1 auto", minWidth: "max-content",
    ...style,
  };
  if (to) return <Link to={to} className="pl-btn-accent" style={btnStyle}>{children}</Link>;
  return <button className="pl-btn-accent" onClick={onClick} style={btnStyle}>{children}</button>;
}

// ─────────────────────────────────────────────────────────────────────
// PAGE COMPONENT
// ─────────────────────────────────────────────────────────────────────
export default function HeroPage({ isDarkMode }) {
  const T = isDarkMode ? DARK : LIGHT;
  const navigate = useNavigate();

  const handleLoadBoard = (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) navigate("/load-board");
      else navigate("/login?redirect=/load-board&reason=loadboard");
    } catch {
      navigate("/login?redirect=/load-board&reason=loadboard");
    }
  };

  const services = [
    { icon:"📋", title:"Live Load Board",          desc:"Real-time oversize load listings across all 50 states. See rates, permit details and route maps — apply with one tap.", page:"loadboard" },
    { icon:"🛡️", title:"Verified Driver Network",  desc:"Every escort driver is ID-checked, certification-verified and rated by real companies. No guesswork, no risk.", page:"login" },
    { icon:"🎯", title:"Dispatcher Console",        desc:"Live GPS tracking, escort team management and AI-powered dispatch — run every job from one screen.", page:"dispatcher" },
    { icon:"📚", title:"Training & Certification",  desc:"State-by-state certification courses, PPCA exam prep and route planning tools to keep your credentials current.", page:"training" },
    { icon:"🛒", title:"Equipment Marketplace",     desc:"Buy and sell pilot car gear — trucks, light bars, high-poles, flags and safety equipment — from trusted sellers.", page:"marketplace" },
    { icon:"💬", title:"Chat, Invoicing & Pay",     desc:"Message drivers and companies instantly, send invoices, and get paid faster — without leaving the platform.", page:"inbox" },
  ];

  const trustPillars = [
    { icon:"✅", title:"Verified, Not Just Listed", desc:"We verify identity, certifications and insurance before any driver goes live. Companies see proof, not promises." },
    { icon:"🧩", title:"Everything In One Place",   desc:"Loads, drivers, dispatch, training, gear and payments — no juggling five websites and a Facebook group." },
    { icon:"🗺️", title:"Coast-to-Coast Coverage",   desc:"Certified escort drivers in all 50 states, with state-specific permit and certification knowledge built in." },
    { icon:"⚡", title:"Faster Jobs, Faster Pay",   desc:"Real-time matching gets loads covered in minutes, and built-in invoicing gets drivers paid without the chase." },
  ];

  const tickerItems = ["LOAD BOARD","VERIFIED DRIVERS","DISPATCH","TRAINING","MARKETPLACE","INVOICING"];

  return (
    <div style={{ background: T.bg }}>
      <style>{`
        @keyframes plPulseDot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.5; transform:scale(0.85); } }
        .pl-card:hover { transform: translateY(-3px); box-shadow: 0 12px 30px rgba(13,31,60,0.14); }
        .pl-btn:hover { filter: brightness(1.08); }
        .pl-btn-ghost:hover { border-color: ${T.accent} !important; color: ${T.accent} !important; }
        .pl-btn-accent:hover { background: ${T.accent} !important; color: #fff !important; }
        .pl-btn:focus-visible, .pl-btn-ghost:focus-visible, .pl-card:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 2px; }
        .pl-partner-logo:hover { filter: grayscale(0) !important; opacity: 1 !important; border-color: ${T.accent}55 !important; }
        @media (prefers-reduced-motion: reduce) {
          .pl-card, .pl-btn, .pl-btn-ghost, .pl-partner-logo { transition: none !important; }
        }
      `}</style>

      {/* ══════════════════════════ HERO ══════════════════════════
          The convoy photo runs full-bleed behind the hero. A left→right
          gradient in the page background color keeps the text side
          perfectly readable, then fades so the trucks and blue sky show
          clean on the right. In dark mode a navy tint sits on top so the
          photo feels embedded in the theme instead of pasted on. */}
      <section style={{
        position: "relative", overflow: "hidden", isolation: "isolate",
        background: T.bg,
        borderBottom: `1px solid ${T.border}`,
      }}>
        {/* Photo layer */}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: `url(${heroTruck})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right center",
          backgroundSize: "cover",
        }} />
        {/* Dark-mode navy tint — pulls the photo into the theme */}
        {isDarkMode && (
          <div aria-hidden="true" style={{
            position: "absolute", inset: 0, zIndex: 0,
            background: `linear-gradient(0deg, ${NAVY}b8, ${NAVY}b8)`,
          }} />
        )}
        {/* Readability gradient — solid bg color on the left, clear on the right */}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0, zIndex: 0,
          background: `linear-gradient(90deg,
            ${T.bg} 0%,
            ${T.bg} 34%,
            ${isDarkMode ? "rgba(13,31,60,0.86)" : "rgba(248,250,252,0.88)"} 52%,
            ${isDarkMode ? "rgba(13,31,60,0.30)" : "rgba(248,250,252,0.15)"} 78%,
            transparent 100%)`,
        }} />
        {/* Bottom fade so the photo melts into the section below */}
        <div aria-hidden="true" style={{
          position: "absolute", left: 0, right: 0, bottom: 0, height: "22%", zIndex: 0,
          background: `linear-gradient(180deg, transparent, ${T.bg})`,
        }} />

        <div style={{
          position: "relative", zIndex: 1,
          maxWidth: 1200, margin: "0 auto",
          padding: "clamp(48px, 7vw, 92px) clamp(20px, 4vw, 40px) clamp(56px, 8vw, 96px)",
        }}>
         

          <div style={{ maxWidth: 600 }}>
            {/* Badge — now blue, matching the single accent */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: isDarkMode ? T.accentBg : "#eff6ff",
              border: `1px solid ${T.accent}44`,
              borderRadius: 99, padding: "5px 14px", marginBottom: "clamp(16px, 3vw, 22px)",
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.accent, display: "inline-block", animation: "plPulseDot 1.5s infinite" }} />
              <span style={{ fontSize: "clamp(11px, 2vw, 12px)", fontWeight: 700, color: isDarkMode ? T.accentL : T.accent }}>
                America's All-in-One Escort Vehicle Platform
              </span>
            </div>

            <h1 style={{
              fontFamily: "'Plus Jakarta Sans',sans-serif",
              fontSize: "clamp(36px, 5.5vw + 1rem, 62px)",
              fontWeight: 900, lineHeight: 1.12, letterSpacing: "-0.02em",
              marginBottom: "clamp(16px, 3vw, 22px)",
            }}>
              <span style={{
                display: "inline-block", color: T.text,
                WebkitTextFillColor: T.text, background: "none",
                WebkitBackgroundClip: "border-box", backgroundClip: "border-box",
              }}>
                Every Escort Car Service.
              </span>
              <br />
              <span style={{ display: "inline-block", color: isDarkMode ? T.accentL : T.accent, WebkitTextFillColor: isDarkMode ? T.accentL : T.accent }}>
                One Trusted Platform.
              </span>
            </h1>

            <p style={{
              fontSize: "clamp(16px, 2vw + 6px, 19px)",
              color: T.textMid, lineHeight: 1.7, maxWidth: 540,
              marginBottom: "clamp(24px, 4vw, 32px)",
            }}>
              PilotLink USA is the <strong style={{ color: T.text }}>only site that covers all your pilot car and escort needs in one place</strong> — verified drivers, a live load board, dispatch, training, equipment and payments. If it moves an oversize load, it lives here.
            </p>

            <div style={{ display: "flex", gap: "clamp(12px, 2vw, 16px)", flexWrap: "wrap", maxWidth: "100%" }}>
              <PrimaryBtn T={T} to="/login">🚛 Get Started — Driver</PrimaryBtn>
              <SecondaryBtn T={T} to="/company-login">🏢 Post a Load — Company</SecondaryBtn>
              <AccentBtn T={T} to="/become-partner">🤝 Become a Partner</AccentBtn>
            </div>

            <div style={{
              display: "flex", gap: "clamp(14px, 2.5vw, 24px)", flexWrap: "wrap",
              marginTop: "clamp(20px, 3vw, 28px)",
              fontSize: 13.5, color: T.textSoft, fontWeight: 600,
            }}>
              {["Verified drivers only", "All 50 states", "Insured & certified", "24/7 support"].map(t => (
                <span key={t} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: T.green, fontWeight: 800 }}>✓</span>{t}
                </span>
              ))}
            </div>
          </div>

          {/* Stats row */}
          <div style={{
            display: "flex", gap: "clamp(24px, 5vw, 48px)", flexWrap: "wrap",
            marginTop: "clamp(40px, 6vw, 60px)",
            borderTop: `1px solid ${T.border}`,
            paddingTop: "clamp(24px, 4vw, 32px)",
            maxWidth: 600,
          }}>
            {[["12,000+","Verified Drivers"],["50","States Covered"],["4.9★","Average Rating"]].map(([v,l]) => (
              <div key={l} style={{ flex: "1 1 min-content", minWidth: 100 }}>
                <div style={{ fontSize: "clamp(22px, 3vw, 28px)", fontWeight: 800, color: isDarkMode ? T.accentL : T.accent, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{v}</div>
                <div style={{ fontSize: "clamp(12px, 1.5vw, 14px)", color: T.textSoft, marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ PARTNERS — big standout banner + become-a-partner CTA ═══════════
          Sits in the quiet gap right under the hero, before the ticker — the
          classic "trusted by" placement, and it uses space that was empty
          rather than adding extra scroll. Sized and framed (border, tinted
          background, shadow) to read as its own destination, not a footnote.
          Swap PARTNER_LOGOS for real logos (as <img> tags) when you have them. */}
      <section style={{
        background: T.bg,
        padding: "clamp(30px, 5vw, 48px) clamp(20px, 4vw, 40px)",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{
            background: isDarkMode ? T.surface : "#eff6ff",
            border: `2px solid ${T.accent}55`,
            borderRadius: 24,
            padding: "clamp(28px, 5vw, 44px)",
            boxShadow: `0 12px 40px ${T.accentBg}`,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
            gap: "clamp(28px, 5vw, 48px)",
            alignItems: "center",
          }}>
            {/* Left: the pitch */}
            <div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: T.accentBg, border: `1px solid ${T.accent}44`,
                borderRadius: 99, padding: "5px 14px", marginBottom: 16,
                fontSize: 12, fontWeight: 800, letterSpacing: ".04em",
                color: isDarkMode ? T.accentL : T.accent,
              }}>
                🤝 PARTNER PROGRAM
              </div>
              <h2 style={{
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: "clamp(24px, 3.5vw + 6px, 36px)", fontWeight: 900,
                color: T.text, lineHeight: 1.2, marginBottom: 14, letterSpacing: "-0.01em",
              }}>
                Become Our Partner
              </h2>
              <p style={{ fontSize: "clamp(14px, 1.8vw, 16px)", color: T.textMid, lineHeight: 1.75, marginBottom: 20, maxWidth: 480 }}>
                Carriers, insurers, equipment suppliers, vehicle vendors, and service providers across the escort industry partner with PilotLink for priority visibility, access to our verified driver network, and a dedicated account manager.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 26 }}>
                {["Priority placement for your loads", "Access to 12,000+ verified drivers", "A dedicated account manager"].map((t) => (
                  <span key={t} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: T.textMid, fontWeight: 600 }}>
                    <span style={{ color: T.green, fontWeight: 800 }}>✓</span>{t}
                  </span>
                ))}
              </div>
              <PrimaryBtn T={T} to="/become-partner" style={{ fontSize: "clamp(14px, 1.6vw, 16px)", padding: "15px 32px", flex: "0 1 auto" }}>
                🤝 Become Our Partner →
              </PrimaryBtn>
            </div>

            {/* Right: social proof — who's already here */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: T.textSoft, marginBottom: 14 }}>
                Trusted by carriers &amp; logistics teams nationwide
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))",
                gap: "clamp(10px, 2vw, 14px)",
              }}>
                {PARTNER_LOGOS.map((p) => (
                  <div
                    key={p.name}
                    className="pl-partner-logo"
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12,
                      padding: "14px 10px", filter: "grayscale(1)", opacity: 0.7,
                      transition: "filter 0.2s ease, opacity 0.2s ease, border-color 0.2s ease",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{p.icon}</span>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: T.textMid, whiteSpace: "nowrap" }}>{p.name}</span>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".03em", textTransform: "uppercase", color: T.textSoft }}>{p.category}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ 5-SECOND STRIP — what we do, at a glance ══════════ */}
      <div style={{
        background: isDarkMode ? "rgba(0,0,0,0.22)" : NAVY,
        borderBottom: `1px solid ${T.border}`,
        padding: "14px clamp(16px, 3vw, 32px)",
        display: "flex", justifyContent: "center", alignItems: "center",
        gap: "clamp(10px, 2vw, 22px)", flexWrap: "wrap",
      }}>
        {tickerItems.map((item, i) => (
          <React.Fragment key={item}>
            <span style={{
              fontSize: "clamp(10.5px, 1.4vw, 12px)", fontWeight: 800,
              letterSpacing: "0.14em", color: "rgba(240,244,248,0.85)",
              fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: "nowrap",
            }}>{item}</span>
            {i < tickerItems.length - 1 && (
              <span aria-hidden="true" style={{ color: "#60a5fa", fontSize: 10 }}>•</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ══════════════════ SERVICES — WHAT WE OFFER ══════════════════ */}
      <section style={{ background: T.bg, padding: "clamp(50px, 8vw, 84px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "clamp(32px, 6vw, 48px)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: isDarkMode ? T.accentL : T.accent, marginBottom: 8 }}>
              Everything We Offer
            </div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(26px, 4vw + 10px, 36px)", fontWeight: 800, color: T.text, letterSpacing: "-0.01em" }}>
              All Your Escort Needs. One Login.
            </h2>
            <p style={{ color: T.textMid, fontSize: "clamp(15px, 1.8vw, 17px)", maxWidth: 560, margin: "14px auto 0", lineHeight: 1.65 }}>
              Stop stitching together job boards, group chats and spreadsheets. PilotLink USA puts the whole escort workflow on one platform.
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
            gap: "clamp(16px, 3vw, 24px)",
          }}>
            {services.map(s => (
              <Card T={T} key={s.title} style={{ padding: "clamp(20px, 3vw, 28px)", cursor: "pointer" }} to={`/${s.page}`}>
                <div style={{
                  width: 52, height: 52, borderRadius: 12,
                  background: T.accentBg, border: `1px solid ${T.accent}33`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26, marginBottom: 16,
                }}>{s.icon}</div>
                <div style={{ fontWeight: 700, fontSize: "clamp(16px, 2vw, 18px)", color: T.text, marginBottom: 8 }}>{s.title}</div>
                <div style={{ fontSize: 14, color: T.textMid, lineHeight: 1.65, marginBottom: 16 }}>{s.desc}</div>
                <span style={{ fontSize: 13, color: isDarkMode ? T.accentL : T.accent, fontWeight: 700 }}>Explore &rarr;</span>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ WHY US — TRUST & VERIFICATION ═══════════════ */}
      <section style={{
        background: isDarkMode ? T.surface : T.surface2,
        borderTop: `1px solid ${T.border}`,
        borderBottom: `1px solid ${T.border}`,
        padding: "clamp(50px, 8vw, 84px) clamp(20px, 4vw, 40px)",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
            gap: "clamp(32px, 5vw, 56px)", alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: isDarkMode ? T.accentL : T.accent, marginBottom: 8 }}>
                Why PilotLink USA
              </div>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(26px, 4vw + 8px, 34px)", fontWeight: 800, color: T.text, lineHeight: 1.2, marginBottom: 16, letterSpacing: "-0.01em" }}>
                The Only Platform Built for the Whole Escort Industry
              </h2>
              <p style={{ color: T.textMid, fontSize: "clamp(15px, 1.8vw, 17px)", lineHeight: 1.75, marginBottom: 20 }}>
                Other sites list jobs. We run the industry's full workflow — and we verify everyone on it. Companies book with confidence because every driver's identity, certifications and insurance are checked before they take a load.
              </p>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                background: T.greenBg, border: `1px solid ${T.green}44`,
                borderRadius: 10, padding: "10px 16px",
              }}>
                <span style={{ fontSize: 18 }}>🛡️</span>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: T.green }}>
                  Verified Driver Guarantee — every profile checked, every cert confirmed
                </span>
              </div>
            </div>

            <div style={{ display: "grid", gap: "clamp(12px, 2vw, 16px)" }}>
              {trustPillars.map(p => (
                <Card T={T} key={p.title} style={{ padding: "clamp(16px, 2.5vw, 22px)", display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <div style={{ fontSize: 22, lineHeight: 1, marginTop: 2 }}>{p.icon}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15.5, color: T.text, marginBottom: 4 }}>{p.title}</div>
                    <div style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.6 }}>{p.desc}</div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ CTA FOOTER ═══════════════════════
          Inverted navy panel in BOTH modes — the classic enterprise
          closing move. Matches the logo navy and the Navbar gradient. */}
      <section style={{
        position: "relative", overflow: "hidden", isolation: "isolate",
        background: `linear-gradient(160deg, ${NAVY} 0%, #1a3358 60%, ${NAVY} 100%)`,
        padding: "clamp(60px, 10vw, 110px) clamp(20px, 4vw, 40px)",
        textAlign: "center",
      }}>
        {/* Subtle dot-grid texture */}
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: "radial-gradient(rgba(240,244,248,0.10) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 40%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 40%, transparent 100%)",
          opacity: 0.5,
        }} />
        {/* One soft blue glow — single accent, kept quiet */}
        <div aria-hidden="true" style={{
          position: "absolute", bottom: "-15%", right: "6%", zIndex: 0,
          width: "min(460px, 55vw)", height: "min(460px, 55vw)", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(59,130,246,0.14) 0%, transparent 70%)",
          filter: "blur(10px)", pointerEvents: "none",
        }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 680, margin: "0 auto" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(59,130,246,0.14)", border: "1px solid rgba(96,165,250,0.35)",
            borderRadius: 99, padding: "6px 16px", marginBottom: "clamp(20px, 3vw, 28px)",
            fontSize: 12, fontWeight: 700, color: "#93c5fd", letterSpacing: ".04em",
          }}>
            12,000+ verified escort pros already run their business here
          </div>

          <h2 style={{
            fontFamily: "'Plus Jakarta Sans',sans-serif",
            fontSize: "clamp(32px, 5vw + 14px, 50px)",
            fontWeight: 900, lineHeight: 1.15, color: "#f0f4f8",
            marginBottom: "clamp(18px, 3vw, 24px)", letterSpacing: "-0.02em",
          }}>
            One Platform.<br/>Every Escort Job in America.
          </h2>

          <p style={{
            color: "rgba(240,244,248,0.75)", fontSize: "clamp(16px, 2vw + 6px, 19px)",
            maxWidth: 560, margin: "0 auto clamp(32px, 5vw, 44px)", lineHeight: 1.75,
          }}>
            Get certified, get verified, get matched with loads and get paid — all in one place. Companies post in minutes; drivers apply in seconds.
          </p>

          <div style={{
            display: "flex", gap: "clamp(14px, 2vw, 18px)", justifyContent: "center",
            flexWrap: "wrap", marginBottom: "clamp(24px, 4vw, 32px)",
          }}>
            <PrimaryBtn T={T} to="/training" style={{ fontSize: "clamp(15px, 1.6vw, 17px)", padding: "16px 36px", background: "#3b82f6" }}>
              📚 Start Training — Free
            </PrimaryBtn>
            <SecondaryBtn T={T} onNavy onClick={handleLoadBoard} style={{ fontSize: "clamp(15px, 1.6vw, 17px)", padding: "16px 36px" }}>
              📋 Browse Open Loads
            </SecondaryBtn>
          </div>

          <div style={{
            display: "flex", gap: "clamp(16px, 3vw, 28px)", justifyContent: "center",
            flexWrap: "wrap", fontSize: 13.5, color: "rgba(240,244,248,0.55)", fontWeight: 600,
          }}>
            {["Free to browse", "No credit card required", "Cancel anytime"].map((t) => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "#34d399", fontWeight: 800 }}>✓</span>{t}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
